import React, { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, Eye, EyeOff, Check, X, Dice5, ToggleLeft, ToggleRight, Calendar, Users, Trophy, BarChart3, ArrowUp, ArrowDown, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { ensureValidLeaderboardSort, getSortableMetricKeys } from '../utils/leaderboardSettings';
import { DEFAULT_ANALYTICS_SECTIONS } from '../utils/analyticsSectionsConfig';

const LEADERBOARD_SORT_LABELS = {
  games: 'Pelit',
  time: 'Aika',
  points: 'Menestyspisteet',
  winRate: 'Voitto-%',
  avgPoints: 'Keskim. MP / peli'
};

// Icon component for PNG images (similar to Lucide icons)
const Icon = ({ src, alt = '', size = 16, className = '' }) => (
  <img 
    src={src} 
    alt={alt} 
    style={{ width: `${size}px`, height: `${size}px` }}
    className={`icon ${className}`}
  />
);

/**
 * AdminSettings - Manage club-specific settings
 * AI and prompts settings are in SuperAdmin dashboard
 */
const AdminSettings = ({ user }) => {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [editValues, setEditValues] = useState({});
    const [revealedKeys, setRevealedKeys] = useState({});
    const [activeCategory, setActiveCategory] = useState('features');
    
    // Feature toggles state
    const [featureSettings, setFeatureSettings] = useState({
        game_nights_admin_only: false
    });
    const [savingFeature, setSavingFeature] = useState({});
    
    // BGG settings state
    const [bggSettings, setBggSettings] = useState({
        bgg_username: '',
        bgg_sync_enabled: false,
        bgg_last_sync: null
    });
    const [savingBgg, setSavingBgg] = useState(false);
    
    // Leaderboard settings state (mittarit + lajittelu)
    const [leaderboardSettings, setLeaderboardSettings] = useState(() =>
        ensureValidLeaderboardSort({
            visibleMetrics: ['time', 'points'],
            sortBy: 'points',
            sortDirection: 'desc'
        })
    );
    const [savingLeaderboard, setSavingLeaderboard] = useState(false);
    
    // Analytics settings state
    const [analyticsSettings, setAnalyticsSettings] = useState({
        sections: []
    });
    const [savingAnalytics, setSavingAnalytics] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    useEffect(() => {
        fetchSettings();
        fetchFeatureSettings();
        fetchBggSettings();
        fetchLeaderboardSettings();
        fetchAnalyticsSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/admin/settings');
            const data = await response.json();
            setSettings(data);
            
            // Initialize edit values
            const values = {};
            data.forEach(s => {
                values[s.setting_key] = s.setting_value;
            });
            setEditValues(values);
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const fetchFeatureSettings = async () => {
        try {
            const response = await apiFetch('/api/club/features');
            if (response.ok) {
                const data = await response.json();
                setFeatureSettings(data);
            }
        } catch (error) {
            console.error('Error fetching feature settings:', error);
        }
    };
    
    const fetchBggSettings = async () => {
        try {
            const response = await apiFetch('/api/club/bgg');
            if (response.ok) {
                const data = await response.json();
                setBggSettings(data);
            }
        } catch (error) {
            console.error('Error fetching BGG settings:', error);
        }
    };
    
    const fetchLeaderboardSettings = async () => {
        try {
            const response = await apiFetch('/api/club/leaderboard-settings');
            if (response.ok) {
                const data = await response.json();
                if (data.visibleMetrics) {
                    setLeaderboardSettings(
                        ensureValidLeaderboardSort({
                            visibleMetrics: data.visibleMetrics,
                            sortBy: data.sortBy,
                            sortDirection: data.sortDirection
                        })
                    );
                }
            }
        } catch (error) {
            console.error('Error fetching leaderboard settings:', error);
        }
    };
    
    const fetchAnalyticsSettings = async () => {
        try {
            const response = await apiFetch('/api/club/analytics-settings');
            if (response.ok) {
                const data = await response.json();
                if (data.sections && data.sections.length > 0) {
                    setAnalyticsSettings(data);
                } else {
                    const defaultData = { sections: DEFAULT_ANALYTICS_SECTIONS.map((s) => ({ ...s })) };
                    setAnalyticsSettings(defaultData);
                    // Auto-save default settings to database
                    try {
                        await apiFetch('/api/club/analytics-settings', {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(defaultData)
                        });
                    } catch (saveError) {
                        console.error('Error auto-saving default analytics settings:', saveError);
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching analytics settings:', error);
            setAnalyticsSettings({ sections: DEFAULT_ANALYTICS_SECTIONS.map((s) => ({ ...s })) });
        }
    };
    
    const saveFeatureToggle = async (key, value) => {
        setSavingFeature(prev => ({ ...prev, [key]: true }));
        try {
            const response = await apiFetch('/api/club/features', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ [key]: value })
            });
            
            if (response.ok) {
                setFeatureSettings(prev => ({ ...prev, [key]: value }));
                setSavingFeature(prev => ({ ...prev, [key]: 'success' }));
                setTimeout(() => setSavingFeature(prev => ({ ...prev, [key]: false })), 1500);
            }
        } catch (error) {
            console.error('Error saving feature:', error);
            setSavingFeature(prev => ({ ...prev, [key]: 'error' }));
            setTimeout(() => setSavingFeature(prev => ({ ...prev, [key]: false })), 2000);
        }
    };
    
    const saveBggSettings = async () => {
        setSavingBgg(true);
        try {
            const response = await apiFetch('/api/club/bgg', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bggSettings)
            });
            
            if (response.ok) {
                setSavingBgg('success');
                setTimeout(() => setSavingBgg(false), 1500);
            }
        } catch (error) {
            console.error('Error saving BGG settings:', error);
            setSavingBgg('error');
            setTimeout(() => setSavingBgg(false), 2000);
        }
    };
    
    const saveLeaderboardSettings = async () => {
        setSavingLeaderboard(true);
        try {
            const response = await apiFetch('/api/club/leaderboard-settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(leaderboardSettings)
            });
            
            if (response.ok) {
                setSavingLeaderboard('success');
                setTimeout(() => setSavingLeaderboard(false), 1500);
            }
        } catch (error) {
            console.error('Error saving leaderboard settings:', error);
            setSavingLeaderboard('error');
            setTimeout(() => setSavingLeaderboard(false), 2000);
        }
    };
    
    const saveAnalyticsSettings = async () => {
        setSavingAnalytics(true);
        try {
            const response = await apiFetch('/api/club/analytics-settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(analyticsSettings)
            });
            
            if (response.ok) {
                setSavingAnalytics('success');
                setTimeout(() => setSavingAnalytics(false), 1500);
            }
        } catch (error) {
            console.error('Error saving analytics settings:', error);
            setSavingAnalytics('error');
            setTimeout(() => setSavingAnalytics(false), 2000);
        }
    };
    
    const toggleLeaderboardMetric = (metric) => {
        const current = leaderboardSettings.visibleMetrics || [];
        const newMetrics = current.includes(metric)
            ? current.filter(m => m !== metric)
            : [...current, metric];
        setLeaderboardSettings(
            ensureValidLeaderboardSort({ ...leaderboardSettings, visibleMetrics: newMetrics })
        );
    };
    
    const moveAnalyticsSection = (index, direction) => {
        const sections = [...analyticsSettings.sections];
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= sections.length) return;
        
        [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
        sections.forEach((s, i) => s.order = i);
        setAnalyticsSettings({ ...analyticsSettings, sections });
    };
    
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.currentTarget);
    };
    
    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };
    
    const handleDragLeave = (e) => {
        // Only clear if we're actually leaving the element (not just moving to a child)
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverIndex(null);
        }
    };
    
    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverIndex(null);
        
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }
        
        const sections = [...analyticsSettings.sections];
        const draggedSection = sections[draggedIndex];
        
        // Remove dragged item
        sections.splice(draggedIndex, 1);
        
        // Insert at new position
        sections.splice(dropIndex, 0, draggedSection);
        
        // Update order values
        sections.forEach((section, idx) => {
            section.order = idx;
        });
        
        setAnalyticsSettings({ ...analyticsSettings, sections });
        setDraggedIndex(null);
    };
    
    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };
    
    const toggleAnalyticsSection = (sectionId, field) => {
        const sections = analyticsSettings.sections.map(s => 
            s.id === sectionId ? { ...s, [field]: !s[field] } : s
        );
        setAnalyticsSettings({ ...analyticsSettings, sections });
    };

    const handleSave = async (key) => {
        setSaving(prev => ({ ...prev, [key]: true }));
        
        try {
            const response = await fetch(`/api/admin/settings/${key}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: editValues[key] })
            });
            
            if (response.ok) {
                // Update local state
                setSettings(prev => prev.map(s => 
                    s.setting_key === key ? { ...s, setting_value: editValues[key] } : s
                ));
                // Show success briefly
                setSaving(prev => ({ ...prev, [key]: 'success' }));
                setTimeout(() => setSaving(prev => ({ ...prev, [key]: false })), 1500);
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            console.error('Error saving setting:', error);
            setSaving(prev => ({ ...prev, [key]: 'error' }));
            setTimeout(() => setSaving(prev => ({ ...prev, [key]: false })), 2000);
        }
    };

    const handleReveal = async (key) => {
        if (revealedKeys[key]) {
            setRevealedKeys(prev => ({ ...prev, [key]: false }));
            return;
        }
        
        try {
            const response = await fetch(`/api/admin/settings/${key}?reveal=true`);
            const data = await response.json();
            setEditValues(prev => ({ ...prev, [key]: data.setting_value }));
            setRevealedKeys(prev => ({ ...prev, [key]: true }));
        } catch (error) {
            console.error('Error revealing setting:', error);
        }
    };


    // Group settings by category
    const dbCategories = [...new Set(settings.map(s => s.category))];
    const filteredSettings = settings.filter(s => s.category === activeCategory);
    
    // Static tabs - AI and prompts are in SuperAdmin dashboard, not here
    const clubAdminTabs = ['features', 'bgg', 'leaderboard', 'analytics', 'general'];
    
    // Filter DB categories - exclude AI/prompts (those are in SuperAdmin dashboard)
    const filteredDbCategories = dbCategories.filter(cat => !['ai', 'prompts', 'email'].includes(cat));
    
    const allCategories = [...new Set([...clubAdminTabs, ...filteredDbCategories])];

    const getCategoryLabel = (cat) => {
        const labels = {
            'features': 'Ominaisuudet',
            'bgg': 'BGG',
            'leaderboard': 'Leaderboard',
            'analytics': 'Analyysit',
            'ai': '🤖 AI-asetukset',
            'prompts': '💬 Promptit',
            'general': '⚙️ Yleiset',
            'email': '📧 Sähköposti',
            'other': '📦 Muut'
        };
        return labels[cat] || cat;
    };
    
    const getCategoryIcon = (cat) => {
        switch (cat) {
            case 'features': return <ToggleLeft size={16} />;
            case 'bgg': return <Dice5 size={16} />;
            case 'leaderboard': return <Trophy size={16} />;
            case 'analytics': return <BarChart3 size={16} />;
            default: return null;
        }
    };

    const getSaveButtonContent = (key) => {
        if (saving[key] === true) return <RefreshCw size={16} className="spin" />;
        if (saving[key] === 'success') return <Check size={16} style={{ color: '#27ae60' }} />;
        if (saving[key] === 'error') return <X size={16} style={{ color: '#e74c3c' }} />;
        return <Save size={16} />;
    };

    if (loading) {
        return (
            <div className="admin-settings loading">
                <RefreshCw size={24} className="spin" />
                <p>Ladataan asetuksia...</p>
            </div>
        );
    }

    return (
        <div className="admin-settings">
            <div className="settings-header">
                <h2><Settings size={24} /> Klubin asetukset</h2>
            </div>

            {/* Category Tabs */}
            <div className="settings-tabs">
                {allCategories.map(cat => (
                    <button
                        key={cat}
                        className={`tab ${activeCategory === cat ? 'active' : ''}`}
                        onClick={() => setActiveCategory(cat)}
                    >
                        {getCategoryIcon(cat)} {getCategoryLabel(cat)}
                    </button>
                ))}
            </div>
            
            {/* Features Section - using settings-list style */}
            {activeCategory === 'features' && (
                <div className="settings-list">
                    {/* Game Nights Admin Only - Toggle */}
                    <div className="setting-item">
                        <div className="setting-info">
                            <span className="description">
                                <Calendar size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                Ehdota peli-iltaa vain Admin
                            </span>
                            <span className="help-text">
                                Kun päällä, vain ylläpitäjät voivat luoda uusia peli-iltaehdotuksia. Kaikki jäsenet voivat silti ilmoittautua.
                            </span>
                        </div>
                        <div className="setting-input">
                            <button 
                                className={`toggle-button-inline ${featureSettings.game_nights_admin_only ? 'active' : ''}`}
                                onClick={() => saveFeatureToggle('game_nights_admin_only', !featureSettings.game_nights_admin_only)}
                                disabled={savingFeature.game_nights_admin_only === true}
                            >
                                {savingFeature.game_nights_admin_only === true ? (
                                    <RefreshCw size={18} className="spin" />
                                ) : featureSettings.game_nights_admin_only ? (
                                    <><ToggleRight size={24} /> <span>Päällä</span></>
                                ) : (
                                    <><ToggleLeft size={24} /> <span>Pois</span></>
                                )}
                            </button>
                        </div>
                    </div>
                    
                    {/* Show feature-related settings from DB */}
                    {filteredSettings.map(setting => (
                        <div key={setting.setting_key} className="setting-item">
                            <div className="setting-info">
                                <span className="description">{setting.description || setting.setting_key}</span>
                            </div>
                            <div className="setting-input">
                                <input
                                    type="text"
                                    value={editValues[setting.setting_key] || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                        ...prev,
                                        [setting.setting_key]: e.target.value
                                    }))}
                                />
                                <button 
                                    onClick={() => handleSave(setting.setting_key)}
                                    disabled={saving[setting.setting_key] === true}
                                    className="btn-save"
                                >
                                    {getSaveButtonContent(setting.setting_key)}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            {/* BGG Section - using settings-list style */}
            {activeCategory === 'bgg' && (
                <div className="settings-list">
                    {/* BGG Username */}
                    <div className="setting-item">
                        <div className="setting-info">
                            <span className="description">
                                <Users size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                BGG-käyttäjänimi
                            </span>
                            <span className="help-text">
                                Käyttäjänimi, jonka kokoelmasta pelit haetaan.
                            </span>
                        </div>
                        <div className="setting-input">
                            <input 
                                type="text"
                                value={bggSettings.bgg_username || ''}
                                onChange={e => setBggSettings({...bggSettings, bgg_username: e.target.value})}
                                placeholder="esim. peliklubi123"
                            />
                        </div>
                    </div>
                    
                    {/* Auto Sync Toggle */}
                    <div className="setting-item">
                        <div className="setting-info">
                            <span className="description">
                                <RefreshCw size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                Automaattinen synkronointi
                            </span>
                            <span className="help-text">
                                Synkronoi pelit automaattisesti BGG:stä päivittäin.
                            </span>
                        </div>
                        <div className="setting-input">
                            <button 
                                className={`toggle-button-inline ${bggSettings.bgg_sync_enabled ? 'active' : ''}`}
                                onClick={() => setBggSettings({...bggSettings, bgg_sync_enabled: !bggSettings.bgg_sync_enabled})}
                            >
                                {bggSettings.bgg_sync_enabled ? (
                                    <><ToggleRight size={24} /> <span>Päällä</span></>
                                ) : (
                                    <><ToggleLeft size={24} /> <span>Pois</span></>
                                )}
                            </button>
                        </div>
                    </div>
                    
                    {/* Last Sync Info */}
                    {bggSettings.bgg_last_sync && (
                        <div className="setting-item">
                            <div className="setting-info">
                                <span className="description">Viimeisin synkronointi</span>
                            </div>
                            <div className="setting-input">
                                <span style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
                                    {new Date(bggSettings.bgg_last_sync).toLocaleString('fi-FI')}
                                </span>
                            </div>
                        </div>
                    )}
                    
                    {/* Show BGG-related settings from DB */}
                    {filteredSettings.map(setting => (
                        <div key={setting.setting_key} className="setting-item">
                            <div className="setting-info">
                                <span className="description">{setting.description || setting.setting_key}</span>
                            </div>
                            <div className="setting-input">
                                <input
                                    type="text"
                                    value={editValues[setting.setting_key] || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                        ...prev,
                                        [setting.setting_key]: e.target.value
                                    }))}
                                />
                                <button 
                                    onClick={() => handleSave(setting.setting_key)}
                                    disabled={saving[setting.setting_key] === true}
                                    className="btn-save"
                                >
                                    {getSaveButtonContent(setting.setting_key)}
                                </button>
                            </div>
                        </div>
                    ))}
                    
                    {/* Save Button */}
                    <div className="setting-item" style={{ borderBottom: 'none', paddingTop: '1rem' }}>
                        <div className="setting-info"></div>
                        <div className="setting-input">
                            <button 
                                onClick={saveBggSettings}
                                disabled={savingBgg === true}
                                className="btn-primary"
                                style={{ minWidth: '150px' }}
                            >
                                {savingBgg === true ? (
                                    <><RefreshCw size={16} className="spin" /> Tallennetaan...</>
                                ) : savingBgg === 'success' ? (
                                    <><Check size={16} /> Tallennettu!</>
                                ) : (
                                    <><Save size={16} /> Tallenna BGG-asetukset</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Leaderboard Section */}
            {activeCategory === 'leaderboard' && (
                <div className="settings-list">
                    <div className="setting-item">
                        <div className="setting-info">
                            <span className="description">
                                <Trophy size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                Näytettävät mittarit
                            </span>
                            <span className="help-text">
                                Valitse mitkä mittarit näytetään Etusivun leaderboard-taulukossa.
                            </span>
                        </div>
                    </div>
                    
                    {[
                        { key: 'time', label: 'Aika', description: 'Kokonaispeliaika' },
                        { key: 'points', label: 'Menestyspisteet', description: 'Kokonaismenestyspisteet' },
                        { key: 'winRate', label: 'Voittoprosentti per oma peli', description: 'Voittoprosentti omissa peleissä' },
                        { key: 'avgPoints', label: 'Keskimääräinen voittopisteiden määrä per pelattu peli', description: 'Keskimäärin ansaitut pisteet per peli' }
                    ].map(metric => (
                        <div key={metric.key} className="setting-item">
                            <div className="setting-info">
                                <span className="description" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {metric.icon && <Icon src={metric.icon} size={20} />}
                                    {metric.label}
                                </span>
                                <span className="help-text">{metric.description}</span>
                            </div>
                            <div className="setting-input">
                                <button 
                                    className={`toggle-button-inline ${(leaderboardSettings.visibleMetrics || []).includes(metric.key) ? 'active' : ''}`}
                                    onClick={() => toggleLeaderboardMetric(metric.key)}
                                >
                                    {(leaderboardSettings.visibleMetrics || []).includes(metric.key) ? (
                                        <><ToggleRight size={24} /> <span>Näkyvissä</span></>
                                    ) : (
                                        <><ToggleLeft size={24} /> <span>Piilotettu</span></>
                                    )}
                                </button>
                            </div>
                        </div>
                    ))}
                    
                    <div className="setting-item">
                        <div className="setting-info">
                            <span className="description">
                                <ArrowUp size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                Lajittelu
                            </span>
                            <span className="help-text">
                                Valitse sarake ja suunta etusivun leaderboard-järjestykselle. Vain näkyvät sarakkeet (ja Pelit) ovat valittavissa.
                            </span>
                        </div>
                        <div className="setting-input" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <select
                                className="setting-select"
                                value={leaderboardSettings.sortBy}
                                onChange={(e) =>
                                    setLeaderboardSettings(
                                        ensureValidLeaderboardSort({
                                            ...leaderboardSettings,
                                            sortBy: e.target.value
                                        })
                                    )
                                }
                                aria-label="Lajittelusarake"
                            >
                                {getSortableMetricKeys(leaderboardSettings.visibleMetrics).map((key) => (
                                    <option key={key} value={key}>
                                        {LEADERBOARD_SORT_LABELS[key] || key}
                                    </option>
                                ))}
                            </select>
                            <select
                                className="setting-select"
                                value={leaderboardSettings.sortDirection}
                                onChange={(e) =>
                                    setLeaderboardSettings({
                                        ...leaderboardSettings,
                                        sortDirection: e.target.value
                                    })
                                }
                                aria-label="Lajittelusuunta"
                            >
                                <option value="desc">Laskeva (suurin ensin)</option>
                                <option value="asc">Nouseva (pienin ensin)</option>
                            </select>
                        </div>
                    </div>
                    
                    {/* Save Button */}
                    <div className="setting-item" style={{ borderBottom: 'none', paddingTop: '1rem' }}>
                        <div className="setting-info"></div>
                        <div className="setting-input">
                            <button 
                                onClick={saveLeaderboardSettings}
                                disabled={savingLeaderboard === true}
                                className="btn-primary"
                                style={{ minWidth: '150px' }}
                            >
                                {savingLeaderboard === true ? (
                                    <><RefreshCw size={16} className="spin" /> Tallennetaan...</>
                                ) : savingLeaderboard === 'success' ? (
                                    <><Check size={16} /> Tallennettu!</>
                                ) : (
                                    <><Save size={16} /> Tallenna Leaderboard-asetukset</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Analytics Section */}
            {activeCategory === 'analytics' && (
                <div className="settings-list">
                    <div className="setting-item">
                        <div className="setting-info">
                            <span className="description">
                                <BarChart3 size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                Analyysien näkyvyys ja järjestys
                            </span>
                            <span className="help-text">
                                Määritä mitkä analyysit näytetään ja missä järjestyksessä. Voit myös määrittää ovatko ne oletuksena avattuna vai kiinni.
                            </span>
                        </div>
                    </div>
                    
                    {analyticsSettings.sections && analyticsSettings.sections
                        .sort((a, b) => a.order - b.order)
                        .map((section, index) => (
                        <div 
                            key={section.id} 
                            className="setting-item"
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            style={{ 
                                opacity: draggedIndex === index ? 0.5 : 1,
                                borderTop: dragOverIndex === index && draggedIndex !== index ? '2px solid var(--primary)' : 'none',
                                cursor: 'move'
                            }}
                        >
                            <div className="setting-info" style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                    <GripVertical size={16} style={{ color: 'var(--text-light)', cursor: 'grab' }} />
                                    <span className="description">{section.name}</span>
                                </div>
                            </div>
                            <div className="setting-input" style={{ flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                                <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                                    <button 
                                        className={`toggle-button-inline ${section.visible ? 'active' : ''}`}
                                        onClick={() => toggleAnalyticsSection(section.id, 'visible')}
                                        style={{ flex: 1 }}
                                    >
                                        {section.visible ? (
                                            <><Eye size={16} /> <span>Näkyvissä</span></>
                                        ) : (
                                            <><EyeOff size={16} /> <span>Piilotettu</span></>
                                        )}
                                    </button>
                                    <button 
                                        className={`toggle-button-inline ${section.expanded ? 'active' : ''}`}
                                        onClick={() => toggleAnalyticsSection(section.id, 'expanded')}
                                        style={{ flex: 1 }}
                                    >
                                        {section.expanded ? (
                                            <><ChevronUp size={16} /> <span>Avattuna</span></>
                                        ) : (
                                            <><ChevronDown size={16} /> <span>Kiinni</span></>
                                        )}
                                    </button>
                                </div>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                    <button 
                                        className="btn-secondary"
                                        onClick={() => moveAnalyticsSection(index, 'up')}
                                        disabled={index === 0}
                                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                    >
                                        <ArrowUp size={14} />
                                    </button>
                                    <button 
                                        className="btn-secondary"
                                        onClick={() => moveAnalyticsSection(index, 'down')}
                                        disabled={index === analyticsSettings.sections.length - 1}
                                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                    >
                                        <ArrowDown size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {/* Save Button */}
                    <div className="setting-item" style={{ borderBottom: 'none', paddingTop: '1rem' }}>
                        <div className="setting-info"></div>
                        <div className="setting-input">
                            <button 
                                onClick={saveAnalyticsSettings}
                                disabled={savingAnalytics === true}
                                className="btn-primary"
                                style={{ minWidth: '150px' }}
                            >
                                {savingAnalytics === true ? (
                                    <><RefreshCw size={16} className="spin" /> Tallennetaan...</>
                                ) : savingAnalytics === 'success' ? (
                                    <><Check size={16} /> Tallennettu!</>
                                ) : (
                                    <><Save size={16} /> Tallenna Analyysit-asetukset</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Settings List */}
            {activeCategory !== 'features' && activeCategory !== 'bgg' && activeCategory !== 'leaderboard' && activeCategory !== 'analytics' && (
            <div className="settings-list">
                {filteredSettings.map(setting => (
                    <div key={setting.setting_key} className="setting-item">
                        <div className="setting-info">
                            <span className="description">{setting.description || setting.setting_key}</span>
                        </div>
                        <div className="setting-input">
                            {setting.is_sensitive ? (
                                <div className="sensitive-input">
                                    <input
                                        type={revealedKeys[setting.setting_key] ? 'text' : 'password'}
                                        value={editValues[setting.setting_key] || ''}
                                        onChange={(e) => setEditValues(prev => ({
                                            ...prev,
                                            [setting.setting_key]: e.target.value
                                        }))}
                                        placeholder="********"
                                    />
                                    <button 
                                        onClick={() => handleReveal(setting.setting_key)}
                                        className="btn-icon"
                                        title={revealedKeys[setting.setting_key] ? 'Piilota' : 'Näytä'}
                                    >
                                        {revealedKeys[setting.setting_key] ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            ) : setting.setting_key.startsWith('prompt_') ? (
                                <textarea
                                    value={editValues[setting.setting_key] || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                        ...prev,
                                        [setting.setting_key]: e.target.value
                                    }))}
                                    rows={12}
                                    className="prompt-textarea"
                                />
                            ) : setting.setting_key.includes('_date') ? (
                                // Date picker for season dates (MM-DD format)
                                <div className="date-picker-input">
                                    <input
                                        type="date"
                                        value={`2024-${editValues[setting.setting_key] || '01-01'}`}
                                        onChange={(e) => {
                                            // Extract MM-DD from the date
                                            const date = e.target.value;
                                            if (date) {
                                                const mmdd = date.slice(5); // Remove YYYY-
                                                setEditValues(prev => ({
                                                    ...prev,
                                                    [setting.setting_key]: mmdd
                                                }));
                                            }
                                        }}
                                    />
                                    <span className="date-display">
                                        {editValues[setting.setting_key] || '01-01'}
                                    </span>
                                </div>
                            ) : setting.setting_key.includes('_timeout') ? (
                                // Number input for timeouts
                                <div className="number-input-group">
                                    <input
                                        type="number"
                                        min="1"
                                        max="1440"
                                        value={editValues[setting.setting_key] || '120'}
                                        onChange={(e) => setEditValues(prev => ({
                                            ...prev,
                                            [setting.setting_key]: e.target.value
                                        }))}
                                    />
                                    <span className="unit">min</span>
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    value={editValues[setting.setting_key] || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                        ...prev,
                                        [setting.setting_key]: e.target.value
                                    }))}
                                />
                            )}
                            <button 
                                onClick={() => handleSave(setting.setting_key)}
                                disabled={saving[setting.setting_key] === true}
                                className="btn-save"
                            >
                                {getSaveButtonContent(setting.setting_key)}
                            </button>
                        </div>
                        <div className="setting-meta">
                            {setting.updated_at && (
                                <span>Päivitetty: {new Date(setting.updated_at).toLocaleString('fi-FI')}</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            )}
        </div>
    );
};

export default AdminSettings;

